import { createBrandApi, getManufacturersAPI, getMergedBrandApi } from "api";
import { useCallback, useState, useEffect, useMemo } from "react";
import { useMutation } from "react-query";
import { LabelledAutocomplete, LabelledInput, LabelledSelect } from "../../Fields";
import useAdBlockStore from "store/AdBlockStore";
import { useCategoryStore } from "store/CategoryStore";
import { useAuthContext } from "contexts";
import { Space, Typography, Alert, Button } from "antd";

const CreateBrandPanel = () => {
  const toAddBrand = useAdBlockStore(state => state.toAddBrand);
  const [brandName, setBrandName] = useState(toAddBrand || "");
  const [isLoading, setIsLoading] = useState(false);
  const [category, setCategory] = useState("");
  const [manufacturerValue, setManufacturerValue] = useState("");
  const [currentManufacturer, setCurrentManufacturer] = useState(null);
  const [manufacturerOptions, setManufacturerOptions] = useState([]);
  const [mergedBrand, setMergedBrand] = useState("");
  const categories = useCategoryStore(state => state.categories);
  const fetchCategories = useCategoryStore(state => state.fetchCategories);
  const depth5Categories = useMemo(() => (Array.isArray(categories) ? categories : []).filter(item => item.depth === 5), [categories]);
  const { userInfo } = useAuthContext();

  const createMutation = useMutation((data) => createBrandApi(data));
  const checkMergedMutation = useMutation((name) => getMergedBrandApi(name));

  const updateCurrentProduct = useAdBlockStore(state => state.updateCurrentProduct);
  const currentAdBlockID = useAdBlockStore(state => state.currentAdBlockID);

  // Update brandName whenever toAddBrand changes
  useEffect(() => {
    if (toAddBrand) {
      setBrandName(toAddBrand);
    }
  }, [toAddBrand]);

  useEffect(() => {
    fetchCategories();
  }, []);

  const debounce = useCallback((func, delay) => {
    let timeoutId;
    return (...args) => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => func.apply(null, args), delay);
    };
  }, []);

  const searchManufacturer = async (searchText) => {
    if (!searchText || searchText.length < 2) {
      setManufacturerOptions([]);
      return;
    }
    try {
      const response = await getManufacturersAPI(searchText);
      console.log(response)
      if (response.success && response.payload) {
        const options = response.payload.manufacturer.map(mfg => ({
          value: mfg.alt_i,
          label: mfg.alt_nm
        }));
        setManufacturerOptions(options);
      }
    } catch (error) {
      setManufacturerOptions([]);
    }
  };

  const debouncedSearchBrands = useCallback(debounce(searchManufacturer, 300), [debounce]);

  const handleCheckMerged = async () => {
    setIsLoading(true);
    try {
      const brand = brandName.trim();
      const response = await checkMergedMutation.mutateAsync(brand);
      if (response.success) {
        const mergedBrandName = response.payload?.brand_name;
        if (mergedBrandName) {
          setMergedBrand(mergedBrandName);
          toast.info(`This brand has been merged with ${mergedBrandName}`);
        } else {
          handleCreateBrand();
        }
      } else {
        handleCreateBrand();
      }
    } catch (error) {
      console.error("Error checking merged brand:", error);
    }
    setIsLoading(false);
  };

  const handleCreateBrand = async (overrideBrandName) => {
    setIsLoading(true);
    const nameToUse = overrideBrandName ?? brandName;
    const res = await createMutation.mutateAsync({
      brand_name: nameToUse,
      sub_cat_i: category ? category : null,
      mfg_id: currentManufacturer?.value,
      adblock_uuid: currentAdBlockID
    });
    if (res.success) {
      updateCurrentProduct({
        brand: res.payload,
        brand_nm: res.payload.brand_name || "",
        ext_brand_i: res.payload.ext_id || null,
        sizes: res.payload.sizes.map(size => ({
          id: size.ext_id,
          title: size.size_c,
          max: size.max,
          min: size.min
        }))
      });
    }
    setIsLoading(false);
    useAdBlockStore.setState({ leftPanelView: 'suggestions' });
  }

  return (
    <div>
      <LabelledInput
        label="New Brand Name"
        type={"text"}
        placeholder="Enter product name"
        value={brandName}
        onChange={setBrandName}
        disabled={isLoading || mergedBrand}
      />
      {userInfo.user?.is_superuser &&
      <LabelledSelect
        label="Sub Category"
        placeholder="Select sub category"
        options={depth5Categories.map(item => ({
          value: item.level_i,
          label: item.level_nm,
        }))}
        onChange={(value) => setCategory(value)}
        value={category}
        disabled={isLoading || mergedBrand}
      />}
      <LabelledAutocomplete
        label="Manufacturer"
        placeholder="Select manufacturer"
        onSearch={debouncedSearchBrands}
        options={manufacturerOptions}
        onSelect={(value, option) => {
          setManufacturerValue(option.label);
          setCurrentManufacturer(option);
        }}
        value={manufacturerValue}
        style={{ width: "100%" }}
        disabled={isLoading || mergedBrand}
      />
      <div className="d-flex justify-content-between mt-3">
        <Button 
          onClick={() => useAdBlockStore.setState({ leftPanelView: 'suggestions' })} 
          type="default" 
          disabled={isLoading || mergedBrand}
        >
          Cancel
        </Button>
        <Button onClick={handleCheckMerged} disabled={isLoading || mergedBrand} loading={isLoading}>Create Brand</Button>
      </div>

      {mergedBrand && (
        <div style={{ marginTop: '24px' }}>
          <Alert
            message={
              <Space direction="vertical" size="small" style={{ width: '100%' }}>
                <Typography.Text>
                  This brand has been merged with{' '}
                  <Typography.Text mark>
                    {mergedBrand}
                  </Typography.Text>
                </Typography.Text>
                <Typography.Text type="secondary">
                  Do you want to use the merged brand or create a new one?
                </Typography.Text>
              </Space>
            }
            type="info"
            action={
              <Space direction="vertical">
                <Button
                  size="small"
                  onClick={() => {
                    handleCreateBrand(mergedBrand);
                    setMergedBrand("");
                  }}
                  loading={isLoading}
                >
                  Use Merged Brand
                </Button>
                <Button
                  size="small"
                  onClick={() => handleCreateBrand()}
                  loading={isLoading}
                >
                  Create New Brand
                </Button>
              </Space>
            }
          />
        </div>
      )}
    </div>
  );
}

export default CreateBrandPanel;